import React from 'react';
import { RotateCcw, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import {
  MEMORIAL_PHOTO_ENHANCE_ADVANCED_NOTE,
  MEMORIAL_PHOTO_ENHANCE_BEST_PHOTO_NOTE,
  MEMORIAL_PHOTO_ENHANCE_SAFE_NOTE,
} from '../../content/memorialKeepsakesContent';
import { DEFAULT_IMAGE_EDITS } from '../../utils/memorialPhotoEdits';

function ToggleButton({ active, onClick, children, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-full border px-3 py-1.5 text-[11px] font-medium transition',
        active
          ? 'border-[hsl(152_32%_20%)] bg-[hsl(152_32%_20%)] text-[hsl(42_30%_96%)]'
          : 'border-[hsl(38_14%_86%)] bg-white/80 text-[#5c534a] hover:border-[#8b7355]/40',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {children}
    </button>
  );
}

export function KeepsakeImageEnhanceControls({
  imageEdits,
  onChange,
  onReset,
  onRotateLeft,
  onRotateRight,
  disabled = false,
}) {
  const edits = imageEdits || DEFAULT_IMAGE_EDITS;

  return (
    <div className="space-y-4 rounded-xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_98%)]/80 p-4" data-testid="keepsake-image-enhance">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[#8b7355]">Enhance photo</p>
        <p className="mt-1 text-xs text-[#5c534a]">{MEMORIAL_PHOTO_ENHANCE_SAFE_NOTE}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <ToggleButton
          active={edits.filter === 'black_white'}
          onClick={() => onChange({ filter: edits.filter === 'black_white' ? 'none' : 'black_white' })}
          disabled={disabled}
        >
          Black &amp; white
        </ToggleButton>
        <ToggleButton
          active={edits.filter === 'sepia'}
          onClick={() => onChange({ filter: edits.filter === 'sepia' ? 'none' : 'sepia' })}
          disabled={disabled}
        >
          Warm tone
        </ToggleButton>
      </div>

      <div>
        <label className="text-xs font-medium text-[#5c534a]" htmlFor="keepsake-brightness">
          Brightness
        </label>
        <input
          id="keepsake-brightness"
          type="range"
          min="-50"
          max="50"
          step="1"
          value={edits.brightness ?? 0}
          disabled={disabled}
          onChange={(e) => onChange({ brightness: Number(e.target.value) })}
          className="mt-2 w-full accent-[hsl(152_32%_20%)]"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">{edits.brightness ?? 0}</p>
      </div>

      <div>
        <label className="text-xs font-medium text-[#5c534a]" htmlFor="keepsake-contrast">
          Contrast
        </label>
        <input
          id="keepsake-contrast"
          type="range"
          min="-50"
          max="50"
          step="1"
          value={edits.contrast ?? 0}
          disabled={disabled}
          onChange={(e) => onChange({ contrast: Number(e.target.value) })}
          className="mt-2 w-full accent-[hsl(152_32%_20%)]"
        />
        <p className="mt-1 text-[10px] text-muted-foreground">{edits.contrast ?? 0}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-[#5c534a]">Sharpen</span>
        <ToggleButton active={!edits.sharpen} onClick={() => onChange({ sharpen: 0 })} disabled={disabled}>
          Off
        </ToggleButton>
        <ToggleButton active={!!edits.sharpen} onClick={() => onChange({ sharpen: 1 })} disabled={disabled}>
          Light
        </ToggleButton>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={disabled} onClick={onRotateLeft}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Rotate left
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={disabled} onClick={onRotateRight}>
          <RotateCw className="mr-1 h-3.5 w-3.5" />
          Rotate right
        </Button>
        <Button type="button" size="sm" variant="ghost" className="rounded-full" disabled={disabled} onClick={onReset}>
          Reset image edits
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground">{MEMORIAL_PHOTO_ENHANCE_BEST_PHOTO_NOTE}</p>

      <div className="rounded-lg border border-dashed border-[hsl(38_14%_86%)] bg-white/60 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[#8b7355]">Advanced restoration</p>
        <p className="mt-1 text-[11px] leading-relaxed text-[#5c534a]">{MEMORIAL_PHOTO_ENHANCE_ADVANCED_NOTE}</p>
      </div>
    </div>
  );
}

export default KeepsakeImageEnhanceControls;
